"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { ChevronRight } from "lucide-react";
import { Button } from "../button";
import { scrollCheckoutContentToTop } from "./utils";

type Step = {
  id: string;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
};

type CheckoutStepsContextValue = {
  currentStep: number;
  steps: Step[];
  goToStep: (index: number) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  registerStep: (step: Step, index: number) => void;
  isLastStep: boolean;
  isFirstStep: boolean;
};

const CheckoutStepsContext =
  React.createContext<CheckoutStepsContextValue | null>(null);

function useCheckoutSteps() {
  const context = React.useContext(CheckoutStepsContext);
  if (!context) {
    throw new Error(
      "useCheckoutSteps must be used within CheckoutSteps.Provider",
    );
  }
  return context;
}

type ProviderProps = {
  children: React.ReactNode;
  defaultStep?: number;
  onStepChange?: (step: number) => void;
  onComplete?: () => void;
  autoScroll?: boolean;
  scrollTarget?: React.RefObject<HTMLElement>;
};

function Provider({
  children,
  defaultStep = 0,
  onStepChange,
  onComplete,
  autoScroll = true,
  scrollTarget,
}: ProviderProps) {
  const [currentStep, setCurrentStep] = React.useState(defaultStep);
  const [steps, setSteps] = React.useState<Step[]>([]);

  const visibleSteps = steps.filter((s) => !s.hidden);
  const isLastStep = currentStep === visibleSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Scroll to top of content on step change
  const scrollToTop = React.useCallback(() => {
    if (!autoScroll) return;

    // Small delay to allow DOM update
    setTimeout(() => {
      if (scrollTarget?.current) {
        scrollTarget.current.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Shadow-DOM aware; never scrolls the host window (would yank an
        // embedded page to top instead of the checkout's own container).
        scrollCheckoutContentToTop();
      }
    }, 50);
  }, [autoScroll, scrollTarget]);

  const goToStep = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < visibleSteps.length) {
        setCurrentStep(index);
        onStepChange?.(index);
        scrollToTop();
      }
    },
    [visibleSteps.length, onStepChange, scrollToTop],
  );

  const goToNextStep = React.useCallback(() => {
    if (isLastStep) {
      onComplete?.();
    } else {
      goToStep(currentStep + 1);
    }
  }, [currentStep, isLastStep, goToStep, onComplete]);

  const goToPreviousStep = React.useCallback(() => {
    if (!isFirstStep) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, isFirstStep, goToStep]);

  const registerStep = React.useCallback((step: Step, index: number) => {
    setSteps((prev) => {
      const newSteps = [...prev];
      newSteps[index] = step;
      return newSteps;
    });
  }, []);

  return (
    <CheckoutStepsContext.Provider
      value={{
        currentStep,
        steps: visibleSteps,
        goToStep,
        goToNextStep,
        goToPreviousStep,
        registerStep,
        isLastStep,
        isFirstStep,
      }}
    >
      {children}
    </CheckoutStepsContext.Provider>
  );
}

type CheckoutStepsProps = {
  children: React.ReactNode;
  className?: string;
};

function CheckoutSteps({ children, className }: CheckoutStepsProps) {
  return <div className={cn("space-y-6", className)}>{children}</div>;
}

type BreadcrumbsProps = {
  className?: string;
};

function Breadcrumbs({ className }: BreadcrumbsProps) {
  const { steps, currentStep, goToStep } = useCheckoutSteps();

  return (
    <nav className={cn("flex items-center gap-2 text-sm", className)}>
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-muted-foreground/70" />
          )}
          <button
            onClick={() => index < currentStep && goToStep(index)}
            disabled={index > currentStep || step.disabled}
            className={cn(
              "font-medium transition-colors",
              index === currentStep && "text-foreground",
              index < currentStep &&
                "text-primary hover:text-primary/80 cursor-pointer",
              index > currentStep && "text-muted-foreground/70",
            )}
          >
            {step.label}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
}

type HeaderProps = {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
};

function Header({ title, description, className }: HeaderProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <h1
        className="text-2xl md:text-3xl font-bold text-foreground uppercase tracking-wide"
        style={{
          fontFamily:
            'var(--font-heading, "Founders Grotesk Condensed", "Arial Bold", sans-serif)',
        }}
      >
        {title}
      </h1>
      {description && <p className="text-muted-foreground">{description}</p>}
    </div>
  );
}

type StepProps = {
  id: string;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  className?: string;
};

function Step({ id, label, children, disabled, hidden, className }: StepProps) {
  const { currentStep, steps, registerStep } = useCheckoutSteps();
  const indexRef = React.useRef<number>(-1);

  React.useEffect(() => {
    const existingIndex = steps.findIndex((s) => s.id === id);
    if (existingIndex === -1) {
      indexRef.current = steps.length;
    } else {
      indexRef.current = existingIndex;
    }
    registerStep({ id, label, disabled, hidden }, indexRef.current);
  }, [id, label, disabled, hidden, registerStep]);

  const visibleSteps = steps.filter((s) => !s.hidden);
  const visibleIndex = visibleSteps.findIndex((s) => s.id === id);

  if (hidden || visibleIndex !== currentStep) {
    return null;
  }

  return <div className={className}>{children}</div>;
}

type ContinueButtonProps = {
  children?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
};

function ContinueButton({
  children = "Continue",
  disabled,
  loading,
  className,
}: ContinueButtonProps) {
  const { goToNextStep, isLastStep } = useCheckoutSteps();

  return (
    <Button
      onClick={goToNextStep}
      disabled={disabled || loading}
      className={cn("min-w-32", className)}
      size="lg"
    >
      {loading
        ? "Loading..."
        : children || (isLastStep ? "Complete" : "Continue")}
    </Button>
  );
}

type BackButtonProps = {
  children?: React.ReactNode;
  className?: string;
};

function BackButton({ children = "Back", className }: BackButtonProps) {
  const { goToPreviousStep, isFirstStep } = useCheckoutSteps();

  if (isFirstStep) {
    return null;
  }

  return (
    <Button variant="ghost" onClick={goToPreviousStep} className={className}>
      {children}
    </Button>
  );
}

CheckoutSteps.Provider = Provider;
CheckoutSteps.Breadcrumbs = Breadcrumbs;
CheckoutSteps.Header = Header;
CheckoutSteps.Step = Step;
CheckoutSteps.ContinueButton = ContinueButton;
CheckoutSteps.BackButton = BackButton;

export { CheckoutSteps, useCheckoutSteps };
